import { useCallback, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
	updateScannerRequestParam,
	uploadExamFile,
} from "../../../components/Api/ExamGradingEndpoints";
import { CircularProgress } from "@mui/material";

export default function ReconciliationDialog({
	open,
	setOpen,
	reconciliationState,
	setReconciliationState,
	reqnum,
	fetchRequestDetails,
	token,
	file,
	ccid,
}) {
	const [loading, setLoading] = useState(false);
	const handleClose = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	const handleCloseWithCheck = async () => {
		setLoading(true);
		try {
			const parseFileResponse = await uploadExamFile(token, file, "upsert", ccid);
			if (parseFileResponse.error) {
				setReconciliationState(parseFileResponse.mismatches || []);
			}
			await fetchRequestDetails();
			setOpen(false);
		} catch (error) {
			console.log("Parse File Response:", error);
		}
		setLoading(false);
	};

	const HEAD_CELLS = [
		{ key: "mismatch", label: "FIELD", width: "10%" },
		{ key: "expected", label: "VALUE FROM DATABASE", width: "35%" },
		{ key: "got", label: "VALUE FROM RAW FILE", width: "35%" },
		{ key: "action", label: "ACTION", width: "20%" }, // Placeholder for future actions
	];

	function FixedHeaderTable({ children }) {
		return (
			<TableContainer component={Paper} sx={{ mb: 2 }}>
				<Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
					<TableHead>
						<TableRow>
							{HEAD_CELLS.map((col) => (
								<TableCell key={col.key} sx={{ width: col.width, p: 1, fontWeight: "bold" }}>
									{col.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					{children}
				</Table>
			</TableContainer>
		);
	}

	const handleReconcileMismatchedValue = async (reqnum, mismatch, expected, got) => {
		try {
			const response = await updateScannerRequestParam(
				token,
				ccid,
				reqnum,
				mismatch,
				expected,
				got,
			);

			setReconciliationState((prevState) =>
				prevState.filter((item) => item.mismatch !== response.data.mismatch),
			);
		} catch (error) {
			// Handle unexpected error
			console.error("Error during reconciliation:", error);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== "backdropClick") {
					handleClose();
				}
			}}
			aria-labelledby="unmatched-student-dialog-title"
			fullWidth
			maxWidth="xl"
			disableEscapeKeyDown
		>
			<DialogTitle id="unmatched-student-dialog-title">
				Exam Header Reconciliation Required <br /> REQ:{" "}
				<span className="font-semibold">{reqnum}</span>
			</DialogTitle>

			<DialogContent>
				<FixedHeaderTable>
					<TableBody>
						{reconciliationState.length > 0 ? (
							reconciliationState.map((item, index) => (
								<TableRow key={index}>
									<TableCell>
										<span className="font-semibold">{item.mismatch}</span>
									</TableCell>
									<TableCell>{item.expected}</TableCell>
									<TableCell
										sx={{
											color: "red",
										}}
									>
										{item.got}
									</TableCell>
									<TableCell>
										<Button
											variant="contained"
											color="primary"
											onClick={() =>
												handleReconcileMismatchedValue(
													reqnum,
													item.mismatch,
													item.expected,
													item.got,
												)
											}
										>
											Resolve
										</Button>
									</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell>None</TableCell>
							</TableRow>
						)}
					</TableBody>
				</FixedHeaderTable>
			</DialogContent>

			<DialogActions>
				{reconciliationState.length === 0 ? (
					<Button
						color="primary"
						variant="contained"
						onClick={handleCloseWithCheck}
						disabled={loading}
					>
						{loading ? <CircularProgress size={24} /> : "Continue Processing"}
					</Button>
				) : (
					<Button color="error" onClick={handleClose} disabled={loading}>
						Close Without Reconciliation (Processing will halt)
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
